import React, { useCallback, useMemo } from 'react'
import { $TOWNS } from '@components/$TOWNS/$TOWNS'
import {
    SpaceDeposits,
    useStakingDeposits,
} from '@components/ClaimRewardsPanel/hooks/useStakingData'
import { Countdown } from '@components/Countdown/Countdown'
import { SpaceIcon } from '@components/SpaceIcon'
import { useWithdraw } from '@components/StakeToTownPanel/hooks/useWithdraw'
import { ImageVariants } from '@components/UploadImage/useImageSource'
import {
    Box,
    BoxProps,
    Button,
    FancyButton,
    Icon,
    IconButton,
    MotionBox,
    Paragraph,
    Stack,
    Text,
} from '@ui'
import formatDistance from 'date-fns/formatDistance'
import { Signer } from 'ethers'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { StakableOperator, useFetchOperatorsQuery, useOperatorYield } from 'hooks/useOperatorData'
import { WalletReady } from 'privy/WalletReady'
import { CHANNEL_INFO_PARAMS } from 'routes'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { MotionBoxProps } from 'ui/components/Motion/MotionComponents'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { atoms } from 'ui/styles/atoms.css'
import { vars } from 'ui/styles/vars.css'
import {
    SpaceIdFromSpaceAddress,
    useContractSpaceInfo,
    useDepositsDelegation,
    useDepositsUnlockDate,
    useSpaceDelegationOperator,
    useStakingRewardsQuery,
    useTownsTokenBalance,
} from 'use-towns-client'

import { TokenListSkeleton } from './TokenListSkeleton'

type SpaceRowProps = {
    spaceDeposit: SpaceDeposits
    isNewDeposit?: boolean
    operators: StakableOperator[] | undefined
}

type NewDepositInfo = {
    spaceId: string
    amount: string
    isNewDeposit: boolean
    timestamp: number
}

export const StakedTokensTab = ({ newDeposit }: { newDeposit?: NewDepositInfo }) => {
    const { openPanel } = usePanelActions()
    const { data: abstractAccountAddress } = useMyAbstractAccountAddress()

    const { data: townsBalance } = useTownsTokenBalance({
        address: abstractAccountAddress as `0x${string}`,
    })

    const { data: currentReward } = useStakingRewardsQuery({
        beneficiary: abstractAccountAddress,
        enabled: !!abstractAccountAddress,
    })

    const {
        data: { groupedDeposits, totalPendingWithdrawal, depositsWithPendingWithdrawal },
        isLoading,
        error,
    } = useStakingDeposits({
        owner: abstractAccountAddress,
        enabled: !!abstractAccountAddress,
    })

    // Get unlock dates for each deposit
    const unlockDateQueries = useDepositsUnlockDate(
        depositsWithPendingWithdrawal.map((d) => d.depositId),
    )

    // Get historical delegation data for pending withdrawal deposits
    const { data: delegationMap } = useDepositsDelegation({
        depositIds: depositsWithPendingWithdrawal.map((d) => d.depositId.toString()),
        enabled: depositsWithPendingWithdrawal.length > 0,
    })

    // Create a map of deposit IDs to their unlock dates
    const depositUnlockDates = useMemo(() => {
        const depositToUnlockDate = new Map<bigint, number>()

        depositsWithPendingWithdrawal.forEach((deposit, index) => {
            const unlockDate = unlockDateQueries[index]?.data
            if (unlockDate) {
                depositToUnlockDate.set(deposit.depositId, unlockDate)
            }
        })

        return depositToUnlockDate
    }, [depositsWithPendingWithdrawal, unlockDateQueries])

    // Check if a deposit is new based on the newDeposit prop
    const isNewDepositForSpace = useCallback(
        (spaceAddress: string) => {
            if (!newDeposit) {
                return false
            }
            return (
                newDeposit.spaceId.toLowerCase() === spaceAddress.toLowerCase() &&
                Date.now() - newDeposit.timestamp < 10000 // Consider "new" for 10 seconds
            )
        },
        [newDeposit],
    )

    const { data: operatorsData, isLoading: isLoadingOperators } = useFetchOperatorsQuery()

    if (isLoading || isLoadingOperators) {
        return (
            <Stack centerContent gap="lg" paddingY="sm">
                <TokenListSkeleton />
            </Stack>
        )
    }

    if (error) {
        return (
            <Stack gap="lg" paddingY="lg" alignItems="center">
                <Icon type="warning" size="square_lg" color="error" />
                <Text color="error" textAlign="center">
                    Error loading staked tokens
                </Text>
                <Text size="sm" color="gray2" textAlign="center">
                    {error instanceof Error ? error.message : 'Unknown error'}
                </Text>
            </Stack>
        )
    }

    if (groupedDeposits.length === 0 && !currentReward?.gt(0) && !totalPendingWithdrawal.gt(0)) {
        return (
            <Stack centerContent grow gap="lg" paddingY="lg">
                <Text color="gray2" textAlign="center">
                    No Staked Tokens at this time.
                </Text>
                {townsBalance ? (
                    <FancyButton
                        cta
                        compact
                        borderRadius="full"
                        onClick={() => openPanel(CHANNEL_INFO_PARAMS.STAKE_TO_TOWN)}
                    >
                        Stake
                    </FancyButton>
                ) : null}
            </Stack>
        )
    }

    return (
        <Stack grow gap="sm">
            {!!townsBalance && townsBalance > 0n && (
                <Button
                    size="button_sm"
                    rounded="full"
                    color="cta1"
                    onClick={() => openPanel(CHANNEL_INFO_PARAMS.STAKE_TO_TOWN)}
                >
                    <Icon type="plus" size="square_xxs" color="cta1" />
                    New Stake
                </Button>
            )}
            {groupedDeposits.map((spaceDeposit) => (
                <DepositByTownRow
                    key={spaceDeposit.space}
                    spaceDeposit={spaceDeposit}
                    isNewDeposit={isNewDepositForSpace(spaceDeposit.space)}
                    operators={operatorsData?.operators}
                />
            ))}
            {depositsWithPendingWithdrawal.length > 0 && <Box borderTop />}
            {depositsWithPendingWithdrawal.map((deposit) => {
                const unlockDate = depositUnlockDates.get(deposit.depositId)
                const delegatee = delegationMap?.get(deposit.depositId.toString())
                return (
                    <WithdrawRow
                        key={deposit.depositId.toString()}
                        deposit={deposit}
                        unlockDate={unlockDate}
                        delegatee={delegatee as `0x${string}` | undefined}
                        operators={operatorsData?.operators}
                    />
                )
            })}
        </Stack>
    )
}

export const TownYield = (props: {
    spaceAddress: `0x${string}`
    renderer?: (children: React.ReactNode) => React.ReactNode
}) => {
    const { spaceAddress, renderer } = props

    const { data: currentDelegation, isLoading: isLoadingDelegatee } =
        useSpaceDelegationOperator(spaceAddress)

    if (!currentDelegation || isLoadingDelegatee) {
        return null
    }

    return <OperatorYield operatorAddress={currentDelegation} renderer={renderer} />
}

export const OperatorYield = (props: {
    operatorAddress: `0x${string}`
    renderer?: (children: React.ReactNode) => React.ReactNode
}) => {
    const { operatorAddress, renderer } = props

    const { yield: yieldValue, isLoading: isLoadingYield } = useOperatorYield(operatorAddress)

    if (isLoadingYield) {
        const value = 'fetching...'
        return renderer ? renderer(<>{value}</>) : <>{value}</>
    }

    if (yieldValue === undefined) {
        return null
    }

    const value = (yieldValue * 100)?.toFixed(1)
    return renderer ? renderer(<>{value}%</>) : <>{value}%</>
}

const DepositByTownRow = ({ spaceDeposit, operators, isNewDeposit }: SpaceRowProps) => {
    const { space: spaceAddress, totalStaked } = spaceDeposit
    const { data: spaceInfo } = useContractSpaceInfo(spaceAddress)

    const streamId = useMemo(() => SpaceIdFromSpaceAddress(spaceAddress), [spaceAddress])

    const { openPanel } = usePanelActions()

    // Only apply delegation filter if operators data has loaded
    if (operators !== undefined) {
        const isDelegatedToOperator = operators.some(
            (op) => op.address.toLowerCase() === spaceAddress.toLowerCase(),
        )

        if (isDelegatedToOperator) {
            return null
        }
    }

    const onEditStake = () => {
        openPanel(CHANNEL_INFO_PARAMS.STAKE_TO_TOWN, { spaceId: spaceAddress })
    }

    return (
        <Row
            highlight={isNewDeposit}
            icon={
                <Box>
                    <SpaceIcon
                        background="level1"
                        border="strongFaint"
                        rounded="sm"
                        letterFontSize="sm"
                        width="x4"
                        height="x4"
                        spaceId={streamId}
                        firstLetterOfSpaceName={spaceInfo?.name?.[0] || '?'}
                        overrideBorderRadius="xs"
                        variant={ImageVariants.thumbnail100}
                        fadeIn={false}
                    />
                </Box>
            }
            center={
                <Stack gap="sm">
                    <Paragraph strong color="default">
                        {spaceInfo?.name || 'Loading...'}
                    </Paragraph>
                    <Paragraph size="sm" color="gray2">
                        Yield:{' '}
                        <span className={atoms({ color: 'default' })}>
                            <TownYield spaceAddress={spaceAddress} />
                        </span>
                    </Paragraph>
                </Stack>
            }
            right={
                <Stack horizontal gap="sm">
                    <Box textAlign="right">
                        <Stack gap="sm" alignItems="end">
                            <Paragraph strong color="default" size="lg">
                                <$TOWNS value={totalStaked.toBigInt()} currency="usd" />
                            </Paragraph>
                            <Paragraph color="gray2" size="sm">
                                <$TOWNS value={totalStaked.toBigInt()} />
                            </Paragraph>
                        </Stack>
                    </Box>
                    <Box>
                        <IconButton
                            size="square_xs"
                            rounded="md"
                            color="default"
                            icon="edit"
                            onClick={onEditStake}
                        />
                    </Box>
                </Stack>
            }
            onClick={onEditStake}
        />
    )
}

const Row = ({
    icon,
    center,
    right,
    onClick,
    highlight,
    ...boxProps
}: {
    icon?: React.ReactNode
    center?: React.ReactNode
    right?: React.ReactNode
    highlight?: boolean
    onClick?: () => void
} & Pick<BoxProps, 'padding'>) => {
    return (
        <HighlightBox
            horizontal
            highlight={highlight}
            gap="sm"
            paddingX="sm"
            paddingY="xs"
            insetX="xs"
            alignItems="center"
            borderRadius="sm"
            background="level1"
            minHeight="x6"
            hoverable={!!onClick}
            cursor={onClick ? 'pointer' : undefined}
            onClick={onClick}
            {...boxProps}
        >
            <Stack centerContent width="x6">
                {icon}
            </Stack>
            <Stack grow gap="sm">
                {center}
            </Stack>
            <Stack>{right}</Stack>
        </HighlightBox>
    )
}

const SpaceDelegationDisplay = ({ spaceAddress }: { spaceAddress: `0x${string}` }) => {
    const { data: spaceInfo } = useContractSpaceInfo(spaceAddress)
    const streamId = useMemo(() => SpaceIdFromSpaceAddress(spaceAddress), [spaceAddress])

    return (
        <SpaceIcon
            rounded="sm"
            letterFontSize="sm"
            width="x4"
            height="x4"
            spaceId={streamId}
            firstLetterOfSpaceName={spaceInfo?.name?.[0] || '?'}
            overrideBorderRadius="xs"
            variant={ImageVariants.thumbnail100}
            fadeIn={false}
        />
    )
}

const RoundTownsBadge = (props: { size?: BoxProps['width'] }) => {
    const { size = 'x4' } = props
    return (
        <Box
            centerContent
            padding="sm"
            width={size}
            height={size}
            aspectRatio="1/1"
            background="cta2"
            rounded="full"
            position="relative"
        >
            <svg viewBox="0 0 159 162" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M36.9346 7.52254C39.4332 4.42896 42.8546 2.32934 46.601 1.45022C48.803 0.667996 51.1738 0.242188 53.644 0.242188H137.925C149.543 0.242188 158.96 9.65995 158.96 21.2773V40.9758C158.96 42.0657 158.877 43.1363 158.718 44.1815L158.801 44.1562V45.207C158.801 56.2527 151.846 65.207 140.801 65.207C136.975 65.207 133.875 68.3079 133.875 72.133V119.99C133.875 130.145 133.875 133.408 129.067 138.997L114.33 153.874C114.078 154.19 113.819 154.502 113.555 154.809C109.579 159.421 104.706 161.762 99.5989 161.762H55.6518C50.5448 161.762 45.6713 159.421 41.6961 154.809C37.5868 150.04 36.9346 144.074 36.9346 139.409V125.204C36.9346 116.275 29.6961 109.036 20.7671 109.036C16.4792 109.036 12.367 107.333 9.33497 104.301L5.15137 100.118C1.853 96.8191 0 92.3456 0 87.681V61.845C0 57.8231 1.37844 53.9227 3.90555 50.7939C6.43266 47.6651 36.9346 7.52254 36.9346 7.52254ZM46.6094 21.5742C46.6094 17.6888 49.7591 14.5391 53.6445 14.5391H137.926C141.811 14.5391 144.961 17.6888 144.961 21.5742V41.2727C144.961 45.1581 141.811 48.3078 137.926 48.3078H122.634C118.749 48.3078 115.599 51.4575 115.599 55.3429V106.996C115.599 110.881 112.449 114.031 108.564 114.031H83.0062C79.1208 114.031 75.9711 110.881 75.9711 106.996V55.343C75.9711 51.4576 72.8213 48.3078 68.9359 48.3078H53.6445C49.7591 48.3078 46.6094 45.1581 46.6094 41.2727V21.5742Z"
                    fill="#000a"
                />
            </svg>
        </Box>
    )
}

const WithdrawRow = (props: {
    deposit: {
        depositId: bigint
        pendingWithdrawal: { toBigInt: () => bigint }
    }
    unlockDate?: number
    delegatee?: `0x${string}`
    operators?: StakableOperator[]
}) => {
    const { deposit, unlockDate, delegatee, operators } = props
    const { openPanel } = usePanelActions()

    const { withdraw, isLoading: isWithdrawing } = useWithdraw()
    const isReadyToWithdraw = !unlockDate || unlockDate <= Date.now()

    const onWithdraw = useCallback(
        (args: { depositIds: bigint[]; getSigner: () => Promise<Signer | undefined> }) => {
            withdraw({
                depositIds: args.depositIds,
                getSigner: args.getSigner,
            })
        },
        [withdraw],
    )

    const onRestake = useCallback(() => {
        openPanel(CHANNEL_INFO_PARAMS.REDELEGATE_STAKE, {
            depositId: deposit.depositId.toString(),
        })
    }, [openPanel, deposit.depositId])

    const isPendingWithdrawal = unlockDate && unlockDate > Date.now()

    // Find operator info if delegatee is an operator
    const operatorInfo = operators?.find(
        (op) => op.address.toLowerCase() === delegatee?.toLowerCase(),
    )

    // Check if delegatee is a space (not an operator)
    const isSpaceDelegation = delegatee && !operatorInfo

    return (
        <Row
            key={deposit.depositId.toString()}
            icon={
                <Box centerContent width="x4" height="x4" background="level2" rounded="sm">
                    {isPendingWithdrawal ? (
                        <>
                            <Icon type="time" size="square_sm" color="gray2" />
                        </>
                    ) : (
                        <>
                            {isSpaceDelegation ? (
                                <SpaceDelegationDisplay spaceAddress={delegatee} />
                            ) : (
                                <Icon type="diamond" size="square_sm" color="gray2" />
                            )}
                        </>
                    )}
                </Box>
            }
            center={
                <Stack gap="sm">
                    <Paragraph strong>
                        <$TOWNS value={deposit.pendingWithdrawal.toBigInt()} />
                    </Paragraph>
                    {operatorInfo && (
                        <Paragraph size="sm" color="gray2">
                            Operator:{' '}
                            <span className={atoms({ color: 'default' })}>{operatorInfo.name}</span>
                        </Paragraph>
                    )}

                    {isPendingWithdrawal ? (
                        <Countdown
                            timestamp={unlockDate}
                            renderCountdown={({ countdownMs: countdown }) => (
                                <Paragraph size="sm" color="gray2">
                                    Time left:{' '}
                                    <span className={atoms({ color: 'default' })}>
                                        {formatDistance(0, countdown)}
                                    </span>
                                </Paragraph>
                            )}
                        />
                    ) : (
                        <Paragraph size="sm" color="gray2">
                            Ready to withdraw
                        </Paragraph>
                    )}
                </Stack>
            }
            right={
                <Stack horizontal gap="sm">
                    <WalletReady>
                        {({ getSigner }) => (
                            <Button
                                title={
                                    unlockDate
                                        ? `Withdraw in ${new Date(unlockDate).toLocaleString()}`
                                        : undefined
                                }
                                size="button_sm"
                                color="cta1"
                                rounded="md"
                                disabled={isWithdrawing || !isReadyToWithdraw}
                                onClick={() => {
                                    onWithdraw({
                                        depositIds: [deposit.depositId],
                                        getSigner,
                                    })
                                }}
                            >
                                {isWithdrawing ? <ButtonSpinner /> : 'Withdraw'}
                            </Button>
                        )}
                    </WalletReady>
                    <Button size="button_sm" rounded="md" tone="cta1" onClick={onRestake}>
                        Restake
                    </Button>
                </Stack>
            }
        />
    )
}

const HighlightBox = (props: MotionBoxProps & { highlight?: boolean }) => {
    const { highlight, ...boxProps } = props
    return (
        <MotionBox
            initial={
                highlight
                    ? {
                          opacity: 0,
                          backgroundColor: vars.color.background.level3,
                      }
                    : undefined
            }
            animate={
                highlight
                    ? {
                          opacity: 1,
                          backgroundColor: `var(--background)`,
                      }
                    : undefined
            }
            transition={
                highlight
                    ? {
                          backgroundColor: {
                              delay: 1,
                              duration: 3,
                              ease: 'easeOut',
                          },
                          opacity: {
                              delay: 0,
                              duration: 1,
                              ease: 'easeOut',
                          },
                      }
                    : undefined
            }
            {...boxProps}
        >
            {props.children}
        </MotionBox>
    )
}

export { RoundTownsBadge, Row }
